import type {
    CreateSmoltReportDto,
    DailyWeightTimeSeriesResponseDto,
    SmoltReportCreateResponseDto,
    SmoltReportDetailResponseDto,
    SmoltReportListResponseDto,
    SmoltReportWarningsResponseDto,
    SmoltWeightDataResponseDto,
    WeightDistributionBucketResponseDto,
} from '@/services/types';

import type {
    CreateSmoltReportRequest,
    DailyWeightTimeSeries,
    SmoltReportCreated,
    SmoltReportDetail,
    SmoltReportListItem,
    SmoltReportWarnings,
    SmoltWeightData,
    WeightDistributionBucket,
} from './types';
import {
    mapIncludedParametersToDomain,
    mapLaksvelParametersToApi,
    mapSmoltWelfareDataToDomain,
    type SmoltWelfareWire,
} from './welfareMappers';

export function mapSmoltReportWarningsToDomain(
    dto: SmoltReportWarningsResponseDto
): SmoltReportWarnings {
    return {
        weightLowFrequency: dto.weightLowFrequency,
        weightDataLatency: dto.weightDataLatency,
        dataEndsBeforeDelivery: dto.dataEndsBeforeDelivery,
        weightPeriodTooShort: dto.weightPeriodTooShort,
        welfareLowCoverage: dto.welfareLowCoverage,
        welfareInsufficientSample: dto.welfareInsufficientSample,
    };
}

export function mapSmoltReportListItemToDomain(
    dto: SmoltReportListResponseDto
): SmoltReportListItem {
    return {
        id: dto.id,
        cageId: dto.cageId,
        fishGroupId: dto.fishGroupId ?? null,
        status: dto.status,
        createdAt: new Date(dto.createdAt),
        stockingDate: dto.stockingDate,
        lastFeedingDate: dto.lastFeedingDate ?? null,
        deliveryDate: dto.deliveryDate,
        warnings: mapSmoltReportWarningsToDomain(dto.warnings),
    };
}

export function mapSmoltReportListToDomain(
    dtos: SmoltReportListResponseDto[]
): SmoltReportListItem[] {
    return dtos.map(mapSmoltReportListItemToDomain);
}

export function mapCreateSmoltReportRequestToApi(
    request: CreateSmoltReportRequest
): CreateSmoltReportDto {
    return {
        stockingDate: request.stockingDate,
        deliveryDate: request.deliveryDate,
        ...(request.lastFeedingDate != null && { lastFeedingDate: request.lastFeedingDate }),
        laksvelParameters: mapLaksvelParametersToApi(request.laksvelParameters),
    };
}

export function mapSmoltReportCreateResponseToDomain(
    dto: SmoltReportCreateResponseDto
): SmoltReportCreated {
    return {
        reportId: dto.reportId,
        cageId: dto.cageId,
        fishGroupId: dto.fishGroupId ?? null,
        stockingDate: dto.stockingDate,
        lastFeedingDate: dto.lastFeedingDate ?? null,
        deliveryDate: dto.deliveryDate,
        status: dto.status,
        warnings: mapSmoltReportWarningsToDomain(dto.warnings),
        createdAt: new Date(dto.createdAt),
    };
}

/** API sends fish dimensions in millimetres; domain uses centimetres. */
function millimetersToCentimeters(value: number | null | undefined): number | null {
    if (value == null || !Number.isFinite(value)) {
        return null;
    }
    return value / 10;
}

function mapWeightDistributionBucketToDomain(
    dto: WeightDistributionBucketResponseDto
): WeightDistributionBucket {
    return {
        from: dto.from ?? null,
        to: dto.to ?? null,
        count: dto.count ?? null,
        percentage: dto.percentage ?? null,
    };
}

function mapDailyWeightTimeSeriesToDomain(
    dto: DailyWeightTimeSeriesResponseDto
): DailyWeightTimeSeries {
    return {
        day: dto.day,
        liveWeight: dto.liveWeight ?? null,
        liveWeightTrend: dto.liveWeightTrend ?? null,
        sgr: dto.sgr ?? null,
        growth: dto.growth ?? null,
        fishHeight: millimetersToCentimeters(dto.fishHeight),
        fishLength: millimetersToCentimeters(dto.fishLength),
        cv: dto.cv ?? null,
        numMeasurements: dto.numMeasurements ?? null,
    };
}

function mapSmoltWeightDataToDomain(dto: SmoltWeightDataResponseDto): SmoltWeightData {
    return {
        numMeasurements: dto.numMeasurements,
        liveWeight: dto.liveWeight ?? null,
        liveWeightAtLastFeeding: dto.liveWeightAtLastFeeding ?? null,
        smallestFish: dto.smallestFish ?? null,
        largestFish: dto.largestFish ?? null,
        growth: dto.growth ?? null,
        sgr: dto.sgr ?? null,
        conditionFactor: dto.conditionFactor ?? null,
        cv: dto.cv ?? null,
        distribution: dto.distribution.map(mapWeightDistributionBucketToDomain),
        distributionDay: dto.distributionDay ?? null,
        timeSeries: dto.timeSeries.map(mapDailyWeightTimeSeriesToDomain),
        lastMeasurementDay: dto.lastMeasurementDay ?? null,
    };
}

/** A report detail as it arrives; the parameter names are read as strings. */
export type SmoltReportDetailWire = Omit<
    SmoltReportDetailResponseDto,
    'welfareData' | 'includedParameters'
> & {
    includedParameters: readonly string[];
    welfareData?: SmoltWelfareWire | null;
};

export function mapSmoltReportDetailToDomain(dto: SmoltReportDetailWire): SmoltReportDetail {
    const included = mapIncludedParametersToDomain(dto.includedParameters);
    return {
        id: dto.id,
        status: dto.status,
        stockingDate: dto.stockingDate,
        lastFeedingDate: dto.lastFeedingDate ?? null,
        deliveryDate: dto.deliveryDate,
        createdAt: new Date(dto.createdAt),
        cageId: dto.cageId,
        fishGroupId: dto.fishGroupId ?? null,
        warnings: mapSmoltReportWarningsToDomain(dto.warnings),
        weightData: mapSmoltWeightDataToDomain(dto.weightData),
        welfareData: mapSmoltWelfareDataToDomain(dto.welfareData ?? null, included),
        hasWelfareData: dto.welfareData != null,
    };
}
